import {Card, Divider, MenuItem, Stack, Tab, Tabs} from "@mui/material";
import {LoadingButton} from "@mui/lab";
import {useEffect, useMemo, useState} from "react";
import {
    FormProvider,
    RHFDatePicker,
    RHFSelect,
    RHFTextField
} from "../../../../components/hook-form";
import * as Yup from 'yup';
import {yupResolver} from "@hookform/resolvers/yup/dist/yup";
import {useForm} from "react-hook-form";
import {Box} from "@mui/system";
import RHFNumberField from "../../../../components/hook-form/RHFNumberField";
import RHFCurrencyField from "../../../../components/hook-form/RHFCurencyField";
import {useTranslation} from "react-i18next";
import RHFMultiValueField from "../../../../components/hook-form/RHFMutliValueField";
import {snackActions} from "../../../../components/notistack/SnackbarUtilsConfigurator";
import {createDiscountCode} from "../../../../services/discountCodeServices";
import {convertDateTimeToServerFormat} from "../../../../utils/formatTime";
import useTabs from "../../../../hooks/useTabs";
import RHFDateTimePicker from "../../../../components/hook-form/RHFDateTimePicker";
import Select from 'react-select';
import {useQuery} from '@tanstack/react-query';
import { shopLiteSearch } from "src/services/shopServices";
import { can } from "src/components/permission/Permission";
import { PermissionPath } from "src/components/permission/PermissionPath";
import zIndex from "@mui/material/styles/zIndex";
import RHFPercentAmountField from "src/components/hook-form/RHFPercentAmountField";
import RHFQasedakShareField from "src/components/hook-form/RHFQasedakShareField";
import {useNavigate} from "react-router-dom";
import { PATH_MANAGEMENT } from "src/routes/paths";

const DISCOUNT_CODE_TYPES = [
    { value: 'invoice', label: 'سفارشات' },
    { value: 'pro', label: 'پرو عمومی' },
    { value: 'pro_monthly', label: 'پرو ماهانه' },
    { value: 'pro_three_monthly', label: 'پرو سه ماهه' },
    { value: 'pro_six_monthly', label: 'پرو شش ماهه' },
];

const DiscountCodeNewEditForm = ({isEdit, currentDiscountCode}) => {
    const [shopId, setShopId] = useState([]);
    const {t} = useTranslation()
    const {currentTab, onChangeTab} = useTabs('static');
    const { data:shopSearch,isLoading: shopLoading} =
    useQuery(['liteSearchShop'],shopLiteSearch,{
       enabled: can(PermissionPath.shop.index)
   });
    const shopsData = shopSearch?.data?.data?.shops?.map(shop => ({
        value: shop.id,
        label: shop.name
    }));

    const DiscountCodeSchema = Yup.object().shape({
        discountcode: Yup.object({
            value: Yup.string().test({
                name: 'value',
                test: (value, ctx) => {
                    if(currentTab === 'static' && (value || '').length <= 0) {
                        return ctx.createError({message: 'عنوان کد تخفیف را وارد کنید.'})
                    }
                    return true
                }
            }),
            prefix_random_value: Yup.string().test({
                name: 'prefix_random_value',
                test: (value, ctx) => {
                    if(currentTab === 'dynamic' && (value || '').length <= 0) {
                        return ctx.createError({message: 'پیش عنوان کد تخفیف را وارد کنید.'})
                    }
                    return true
                }
            }),
            number_of_discountcodes: Yup.string().test({
                name: 'number_of_discountcodes',
                test: (value, ctx) => {
                    if(currentTab === 'dynamic' && (value || '').length <= 0) {
                        return ctx.createError({message: 'تعداد کد تخفیف را وارد کنید.'})
                    }
                    return true
                }
            }),
            length_of_random_value: Yup.string().test({
                name: 'length_of_random_value',
                test: (value, ctx) => {
                    if(currentTab === 'dynamic' && (value || '').length <= 0) {
                        return ctx.createError({message: 'طول کد تخفیف را وارد کنید.'})
                    }
                    return true
                }
            }),
            number_of_usable: Yup.string().required('مقدار قابل استفاده را وارد کنید'),
            min_amount: Yup.string().required('کمترین مقدار را وارد کنید.'),
            max_amount: Yup.string().required('بیشترین مقدار را وارد کنید.'),
        }),
        discount: Yup.object({
            value: Yup.string().required('مقدار تخفیف را وارد کنید.'),
            type: Yup.string().required('نوع تخفیف را انتخاب کنید.'),
            qasedak_share: Yup.string().required('سهم قاصدک را وارد کنید.'),
            started_at: Yup.string().required('تاریخ شروع را وارد کنید.'),
            ended_at: Yup.string().required('تاریخ پایان را وارد کنید.'),
        }),
        users: Yup.array().test({
            name: 'private_users_required',
            test: (users, ctx) => {
                if (ctx.parent?.discount?.usability_status === 'private' && (!users || users.length === 0)) {
                    return ctx.createError({message: 'برای کد تخفیف خصوصی حداقل یک کاربر انتخاب کنید.'})
                }
                return true
            }
        })
    });

    const defaultValues = useMemo(
        () => ({
            ...currentDiscountCode,
            users: currentDiscountCode?.users ?? [],
            discount: {
                ...currentDiscountCode?.discount,
                usability_status: currentDiscountCode?.discount?.usability_status ?? 'public',
                type: currentDiscountCode?.discount?.value ?? 'percent',
                shops: ''
            }
        }),
        [currentDiscountCode]
    );
    const navigate = useNavigate()
    const methods = useForm({
        mode: 'onChange',
        resolver: yupResolver(DiscountCodeSchema),
        defaultValues,
    });

    const {
        watch,
        handleSubmit,
        setValue,
        formState: {isSubmitting},
        reset
    } = methods;

    const usabilityStatus = watch('discount.usability_status');

    useEffect(() => {
        if (usabilityStatus !== 'private') {
            setValue('users', [], {shouldValidate: true});
        }
    }, [setValue, usabilityStatus]);

    const onSubmit = async (values) => {
        const {users, ...valuesWithoutUsers} = values;
        const isPrivate = values.discount.usability_status === 'private';
        const response = await createDiscountCode({
            ...valuesWithoutUsers,
            ...(isPrivate ? {users} : {}),
            shops:shopId?.map(item=>item.value),
            discount: {
                ...values.discount,
                started_at: convertDateTimeToServerFormat(values.discount.started_at),
                ended_at: convertDateTimeToServerFormat(values.discount.ended_at),
            }
        })
        if (response.status === 201) {
            snackActions.success(response.data.message)
            navigate(PATH_MANAGEMENT.management.discountCode.root)

        }
    }


    const TABS = [
        { value: 'static', label: t('static'), color: 'success'},
        { value: 'dynamic', label: t('dynamic'), color: 'warning'},
    ];


    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Card>
                <Tabs
                    allowScrollButtonsMobile
                    variant="scrollable"
                    scrollButtons="auto"
                    value={currentTab}
                    onChange={onChangeTab}
                    sx={{ px: 2, bgcolor: 'background.neutral' }}
                >
                    {TABS?.map((tab) => (
                        <Tab
                            disableRipple
                            key={tab.value}
                            value={tab.value}
                            label={
                                <Stack spacing={1} direction="row" alignItems="center">
                                    <div>{tab.label}</div>
                                </Stack>
                            }
                        />
                    ))}
                </Tabs>

                <Divider />
                <Box
                    sx={{
                        padding: 3,
                        display: 'grid',
                        rowGap: 3,
                        columnGap: 2,
                        gridTemplateColumns: {xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)'},
                    }}
                >

                    <RHFPercentAmountField convert={true} typeName={'discount.type'} valueName={'discount.value'} label={'تخفیف '}/>
                    <RHFSelect
                        fullWidth
                        name="discount.usability_status"
                        label={t('usability_status')}
                        InputLabelProps={{shrink: true}}
                        SelectProps={{native: false, sx: {textTransform: 'capitalize'}}}
                    >
                        <MenuItem
                            value={'public'}
                            sx={{
                                mx: 1,
                                my: 0.5,
                                borderRadius: 0.75,
                                typography: 'body2',
                                textTransform: 'capitalize',
                            }}
                        >
                            {t('public')}
                        </MenuItem>
                        <MenuItem
                            value={'private'}
                            sx={{
                                mx: 1,
                                my: 0.5,
                                borderRadius: 0.75,
                                typography: 'body2',
                                textTransform: 'capitalize',
                            }}
                        >
                            {t('private')}
                        </MenuItem>
                    </RHFSelect>
                    {
                        currentTab === 'static' ? <RHFTextField name="discountcode.value" label={'کد'}/> : <>
                            <RHFTextField name="discountcode.prefix_random_value" label={'پیش عنوان'}/>
                            <RHFNumberField name="discountcode.number_of_discountcodes" label={'تعداد کد'}/>
                            <RHFNumberField name="discountcode.length_of_random_value" label={'طول کد'}/>
                        </>
                    }
                      <Select
                            name="shops" placeholder="-- انتخاب واحد --"
                            onChange={(select)=>setShopId(select)}
                            options={shopsData}
                            isDisabled={!can(PermissionPath.shop.index)}
                            isMulti
                            value={shopId}
                        />
                    <RHFNumberField name="discountcode.number_of_usable" label={'دفعات قابل استفاده'}/>
                    <RHFCurrencyField convert={true} name="discountcode.min_amount" label={'کمترین مقدار'} sx={{zIndex:0}} />
                    <RHFCurrencyField convert={true} name="discountcode.max_amount" label={'بیشترین مقدار'}/>

                    <RHFSelect
                        fullWidth
                        name="discountcode.type"
                        label={'نوع کد تخفیف'}
                        InputLabelProps={{shrink: true}}
                        SelectProps={{native: false, sx: {textTransform: 'capitalize'}}}
                    >
                        {DISCOUNT_CODE_TYPES.map((option) => (
                            <MenuItem
                                key={option.value}
                                value={option.value}
                                sx={{
                                    mx: 1,
                                    my: 0.5,
                                    borderRadius: 0.75,
                                    typography: 'body2',
                                    textTransform: 'capitalize',
                                }}
                            >
                                {option.label}
                            </MenuItem>
                        ))}
                    </RHFSelect>


                    <RHFQasedakShareField valueName={'discount.qasedak_share'}  label={'(درصد) سهم قاصدک'}  sx={{zIndex:0}} />
                    <RHFDateTimePicker name="discount.started_at" label={'شروع'}/>
                    <RHFDateTimePicker name="discount.ended_at" label={'پایان'}/>
                                     
                    {
                        usabilityStatus === 'private' && <>
                            <RHFMultiValueField regex={''} name="users" label={t('users')}
                                                placeholder={'شماره تلفن را با Enter از هم جدا کنید'}/>
                        </>
                    }
                </Box>
            </Card>

            <Stack justifyContent="flex-end" direction="row" spacing={2} sx={{mt: 3}}>
                <LoadingButton
                    size="large"
                    variant="contained"
                    loading={isSubmitting}
                    type='submit'
                >
                    {isEdit ? 'ویرایش' : 'ذخیره'}
                </LoadingButton>
            </Stack>
        </FormProvider>
    )
}
export default DiscountCodeNewEditForm;
